import { useId, useState, type CSSProperties } from 'react';
import { MergeFieldInserter } from '../templates/builder/MergeFieldInserter';

/**
 * Fields shared by the sequence step editors (CampaignSequencePage and WorkflowBuilderPage).
 */

/**
 * A text input or textarea with the `{ }` button that inserts a personalisation tag at the cursor
 * (JT1-1911). `tokens` is the set the step's sender resolves; each step type has its own.
 */
export function TokenField({
  value,
  onChange,
  tokens,
  label,
  placeholder,
  multiline,
  rows = 3,
  maxLength,
  disabled,
  style,
}: {
  value: string;
  onChange: (next: string) => void;
  tokens: readonly string[];
  /** Accessible name for the field. */
  label: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const [caret, setCaret] = useState(value.length);
  const common = {
    value,
    'aria-label': label,
    placeholder,
    maxLength,
    disabled,
    style: { flex: 1, ...style },
    onSelect: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setCaret(e.currentTarget.selectionStart ?? value.length),
  };
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: multiline ? 'flex-start' : 'center' }}>
      {multiline ? (
        <textarea {...common} rows={rows} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type="text" {...common} onChange={(e) => onChange(e.target.value)} />
      )}
      {!disabled && <MergeFieldInserter tokens={tokens} onInsert={onChange} value={value} caret={caret} />}
    </div>
  );
}

type TeamMember = { jt_user_id: string; jt_account_id?: string | null; name: string };

/**
 * Saved as the assignee to send the task to whoever owns the booking link, job or deal that started
 * the enrolment, falling back to the campaign owner (JT1-1921). The API resolves the same value.
 */
export const TRIGGER_OWNER_ASSIGNEE = 'trigger_owner';

/**
 * Who a TASK step's JobTetris task goes to (JT1-1873): the campaign owner, the owner of what
 * started the enrolment, or someone picked by name. Stores the person's users_cache key, as the
 * engine expects; null means the campaign owner.
 */
export function TaskAssigneeSelect({
  value,
  onChange,
  users,
  disabled,
  style,
}: {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  users: TeamMember[] | undefined;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const id = useId();
  const triggerOwner = value === TRIGGER_OWNER_ASSIGNEE;
  // Steps saved before the picker could hold a JobTetris account id instead of the person key.
  const match = value && !triggerOwner ? users?.find((u) => u.jt_user_id === value || u.jt_account_id === value) : undefined;
  const selected = match?.jt_user_id ?? value ?? '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <label htmlFor={id} style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
        Assign the task to
      </label>
      <select
        id={id}
        value={selected}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ flex: 1, ...style }}
      >
        <option value="">Campaign owner</option>
        <option value={TRIGGER_OWNER_ASSIGNEE}>Owner of the booking link, job or deal that started it</option>
        {value && !match && !triggerOwner && <option value={value}>Someone no longer in the team</option>}
        {users?.map((u) => (
          <option key={u.jt_user_id} value={u.jt_user_id}>
            {u.name}
          </option>
        ))}
      </select>
    </div>
  );
}
